// import * as Sentry from '@sentry/react';
import { PaginatedResult, PaginationParams } from './';
import { QueryClient } from '@tanstack/react-query';
import { handleSentryErrors } from '../HandleSentryErrors';
import { NoDataFoundError } from '../APIRequestError';
import { useCurrentUserStore } from 'store/UserStore';
import { ZodObject, ZodRawShape } from 'zod';

export interface UsePreFetchQueryParams<
  T,
  TResult extends PaginatedResult<T> = PaginatedResult<T>
> {
  queryClient: QueryClient;
  queryKey: any[];
  fetchFunction: (params: PaginationParams) => Promise<TResult | null>;
  isEntity?: (obj: any) => obj is T;
  functionName: string;
  params: PaginationParams;
  enabled?: boolean;
  displayNotification?: boolean;
  validationSchema?: ZodObject<ZodRawShape>;
}

/**
 * Custom hook for pre-fetching the next page in paginated queries.
 *
 * @param queryClient The tanstack query client.
 * @param queryKey The key for the query.
 * @param fetchFunction The function to fetch paginated data.
 * @param isEntity Function to check if an object is of type T.
 * @param params Parameters for pagination.
 * @param functionName Name of the function being executed.
 *
 */

export const prefetchQuery = async <
  T,
  TResult extends PaginatedResult<T> = PaginatedResult<T>
>({
  queryClient,
  queryKey,
  fetchFunction,
  isEntity,
  params,
  functionName,
  enabled = true,
  displayNotification = false,
  validationSchema,
}: UsePreFetchQueryParams<T, TResult>) => {
  const invalidDataIds: string[] = [];
  const invalidData: T[] = [];
  const validationErrors: any[] = [];
  const user = useCurrentUserStore.getState();
  const handleNoDataFound = () => {};
  const handleInvalidData = () => {};

  if (!enabled) {
    return;
  }

  await queryClient.prefetchQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKey,
    staleTime: 60000 * 60 * 3, // 3 hours,
    queryFn: async () => {
      const data = await fetchFunction(params);

      if (!data || !data.data.length) {
        const error = new NoDataFoundError(
          `No data found for ${functionName}`,
          functionName,
          params
        );
        handleSentryErrors({
          error,
          functionName,
          params,
          displayNotification,
          handleNoDataFound,
          handleInvalidData,
          user,
        });
        return {
          data: [],
          total: 0,
          nextPage: 0,
          invalidData: [],
        } as TResult;
      }

      for (const entity of data.data) {
        if (!isEntity(entity)) {
          if (entity && 'id' in entity) {
            invalidData.push(entity);
            invalidDataIds.push((entity as { id: string }).id);
            if (validationSchema) {
              validationErrors.push({
                id: (entity as { id: string }).id,
                errors: validationSchema?.safeParse(entity).error,
              });
            }
          }
        }
      }

      return { ...data, invalidData: invalidDataIds } as TResult;
    },
    retry: false,
  });
};

export const usePreFetchQuery = prefetchQuery;
